<template>
  <template v-if="editionMode">
    <div class="fm-form-input mt-2">
      <BaseLabelTop
        :target="name"
        :label="label ?? $t('attributes.business_number')"
        :disabled="disabled"
        :required="required"
      />
      <input
        v-model="localModelValue"
        :name="name"
        :disabled="disabled"
        :placeholder="placeholder"
        type="text"
      />
      <BaseShowEditStatusBadge v-if="status !== 'unknown'" :status="status" />
    </div>
    <ErrorContainer :error-key="name" :errors="errors" />
  </template>
  <template v-else>
    <BaseShowLabel
      :label="label ?? $t('attributes.business_number')"
      :model-value="localModelValue"
    />
  </template>
</template>

<script>
import BaseLabelTop from '@c/addf-package/components/BaseLabel/BaseLabelTop.vue'
import BaseShowLabel from '@c/addf-package/components/BaseLabel/BaseShowLabel.vue'
import { Country } from '@/assets/enums'
import ErrorContainer from '@c/addf-package/components/BaseShowEditInput/ErrorContainer.vue'
import BaseShowEditStatusBadge from '@c/addf-package/components/BaseShowEditInput/BaseShowEditStatusBadge.vue'
import { checkBelgianBusinessNumber } from '@u/identifiers/companyIdentifiers'

/**
 * Enterprise number with a check-digit verdict, on the model of
 * BaseShowEditNationalNumberInput (BBSW-11615).
 *
 * The verdict is shown, never enforced: only the Belgian rule is known, so a
 * foreign number gets no verdict at all rather than a red cross, and nothing
 * here blocks the form from being submitted.
 */
export default {
  name: 'BaseShowEditBusinessNumberInput',
  components: {
    BaseLabelTop,
    BaseShowLabel,
    BaseShowEditStatusBadge,
    ErrorContainer
  },
  props: {
    modelValue: {
      type: String,
      default: null
    },
    errors: {
      type: Array,
      default: () => []
    },
    editionMode: {
      type: Boolean,
      default: false
    },
    name: {
      type: String,
      default: 'business_number'
    },
    label: {
      type: String,
      default: null
    },
    /** A valid example: it shows the format expected, digits need no translation. */
    placeholder: {
      type: String,
      default: '0123.456.749'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    /**
     * Verdicts are only given on a Belgian number; see the component doc.
     * When it is empty, a value typed with `BE` in front counts as Belgian.
     */
    country: {
      type: String,
      default: null
    }
  },
  emits: ['update:modelValue'],
  computed: {
    localModelValue: {
      get() {
        return this.modelValue
      },
      set(value) {
        this.$emit('update:modelValue', value)
      }
    },
    /** A contact with no country yet: the `BE` typed in front tells. */
    isBelgian() {
      if (this.country) {
        return this.country === Country.BELGIUM
      }

      return /^\s*BE/i.test(this.modelValue ?? '')
    },
    status() {
      if (!this.isBelgian) {
        return 'unknown'
      }

      return checkBelgianBusinessNumber(this.modelValue)
    }
  }
}
</script>
